import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink } from 'react-router-dom'
import { Download, Package, Layers, Loader2, X } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/utils'
import PeriodFilter, { type Period } from '../components/PeriodFilter'

// Sales Order Details Report — Haitham ops ask 2026-07-27. Companion to
// the existing `/pos-sales` (Sales Summary) page. Row-level breakdown
// by item OR category, computed from `order_items` joined to
// `stock_movements` (for time-accurate historical cost) + orders (for
// the period + channel filter).
//
// Both pages share a two-tab header strip so ops can flip between
// summary and detail without leaving the Sales (POS) area.

type GroupBy = 'item' | 'category'

interface ItemRow {
  id: string
  sku: string
  name_en: string
  name_ar: string
  category_id: string | null
  category_name_en: string | null
  category_name_ar: string | null
  units_sold: number
  gross_revenue_fils: number
  discount_fils: number
  net_revenue_fils: number
  cost_fils: number
  tax_fils: number
}

interface CategoryRow {
  id: string
  slug: string
  name_en: string
  name_ar: string
  units_sold: number
  gross_revenue_fils: number
  discount_fils: number
  net_revenue_fils: number
  cost_fils: number
  tax_fils: number
}

type ChannelScope = 'pos' | 'phone_in' | 'all'
const CHANNEL_OPTIONS: Array<{ value: ChannelScope; label: string }> = [
  { value: 'pos',      label: 'In-store (POS)' },
  { value: 'phone_in', label: 'Phone-in delivery' },
  { value: 'all',      label: 'All till sales' },
]

export default function PosSalesDetail() {
  const [period, setPeriod] = useState<Period>('7d')
  const [groupBy, setGroupBy] = useState<GroupBy>('item')
  const [channel, setChannel] = useState<ChannelScope>('pos')
  const [downloading, setDownloading] = useState(false)
  // Custom date range — when both are filled, overrides the preset and
  // sends `period=custom&from=&to=` to the analytics endpoint (backend
  // already supports this via parsePeriod in analytics.ts). One-sided
  // input (just From, or just To) is ignored so we don't send a
  // half-formed query the backend would silently default to 7d.
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const isCustomRange = Boolean(customFrom && customTo)

  // Backend expects channel=all|delivery|pos; 'phone_in' is a client
  // convenience that scopes to delivery orders rung by a cashier. For
  // the analytics endpoint we already fold phone-in orders under the
  // 'delivery' channel — that matches how sales-overview treats them.
  // Simpler here: map phone_in → delivery, pos → pos, all → all.
  const apiChannel = channel === 'phone_in' ? 'delivery' : channel

  const params = useMemo(() => {
    const p = new URLSearchParams({ group_by: groupBy, channel: apiChannel })
    if (isCustomRange) {
      p.set('period', 'custom')
      p.set('from', customFrom)
      p.set('to', customTo)
    } else {
      p.set('period', period)
    }
    return p
  }, [period, groupBy, apiChannel, isCustomRange, customFrom, customTo])

  const { data, isLoading, error } = useQuery<{
    group_by: GroupBy
    vat_pct: number
    rows: ItemRow[] | CategoryRow[]
  }>({
    queryKey: ['sales-detail', params.toString()],
    queryFn: () => api.get(`/analytics/sales-detail?${params.toString()}`).then(r => r.data),
  })

  async function downloadCsv() {
    setDownloading(true)
    try {
      const res = await api.get(`/analytics/sales-detail/export?${params.toString()}`, {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      // Filename encodes the range so downloads land in Excel with
      // self-describing names — no need for ops to rename them.
      const dateSegment = isCustomRange ? `${customFrom}_to_${customTo}` : period
      a.download = `sales-detail-${groupBy}-${dateSegment}-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }

  // Preset chip click clears the custom range so the two controls
  // don't compete — the user's most recent choice always wins.
  function selectPreset(next: Period) {
    setPeriod(next)
    setCustomFrom('')
    setCustomTo('')
  }

  function clearCustomRange() {
    setCustomFrom('')
    setCustomTo('')
  }

  const totals = (data?.rows ?? []).reduce(
    (acc, r) => ({
      units: acc.units + r.units_sold,
      cost: acc.cost + r.cost_fils,
      gross: acc.gross + r.gross_revenue_fils,
      discount: acc.discount + r.discount_fils,
      net: acc.net + r.net_revenue_fils,
      tax: acc.tax + r.tax_fils,
    }),
    { units: 0, cost: 0, gross: 0, discount: 0, net: 0, tax: 0 },
  )

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-white">Sales (POS)</h1>
        <div className={isCustomRange ? 'opacity-40 pointer-events-none' : ''}>
          <PeriodFilter value={period} onChange={selectPreset} />
        </div>
      </div>

      {/* Tab strip — Summary is the existing /pos-sales page. */}
      <div className="flex items-center gap-1 border-b border-white/5 mb-5">
        <NavLink
          to="/pos-sales"
          end
          className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent"
        >
          Sales Summary
        </NavLink>
        <NavLink
          to="/pos-sales-detail"
          className="px-4 py-2 text-sm font-medium text-white border-b-2 border-[#C41E3A]"
        >
          Sales Detail
        </NavLink>
      </div>

      {/* Filter row — group-by toggle + channel + CSV export */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
          <button
            onClick={() => setGroupBy('item')}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm ${
              groupBy === 'item' ? 'bg-[#C41E3A] text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'
            }`}
          >
            <Package size={14} /> By Item
          </button>
          <button
            onClick={() => setGroupBy('category')}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm ${
              groupBy === 'category' ? 'bg-[#C41E3A] text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'
            }`}
          >
            <Layers size={14} /> By Category
          </button>
        </div>

        <select
          value={channel}
          onChange={e => setChannel(e.target.value as ChannelScope)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C41E3A]"
        >
          {CHANNEL_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* Custom date range — overrides the preset chips above when
            BOTH dates are set. Single-side input is ignored so we don't
            send a half-formed query that the backend silently defaults
            to 7d. Clicking a preset chip clears these back to empty. */}
        <div className="inline-flex items-center gap-2 pl-2 border-l border-white/10">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Range</span>
          <input
            type="date"
            value={customFrom}
            onChange={e => setCustomFrom(e.target.value)}
            max={customTo || undefined}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C41E3A]"
            title="From (inclusive)"
          />
          <span className="text-gray-500">→</span>
          <input
            type="date"
            value={customTo}
            onChange={e => setCustomTo(e.target.value)}
            min={customFrom || undefined}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C41E3A]"
            title="To (inclusive)"
          />
          {(customFrom || customTo) && (
            <button
              onClick={clearCustomRange}
              className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-white/5"
              title="Clear custom range (use preset above)"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex-1" />

        <button
          onClick={downloadCsv}
          disabled={downloading || !data?.rows?.length}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-sm text-gray-300 hover:bg-white/5 disabled:opacity-50"
        >
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Export CSV
        </button>
      </div>

      {/* Totals strip — always visible so ops sees period sums even
          while the table below is scrolling. */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <TotalTile label="Units" value={totals.units.toLocaleString()} />
          <TotalTile label="Cost" value={formatCurrency(totals.cost)} />
          <TotalTile label="Gross" value={formatCurrency(totals.gross)} />
          <TotalTile label="Discount" value={formatCurrency(totals.discount)} />
          <TotalTile label="Net" value={formatCurrency(totals.net)} />
          <TotalTile label={`Tax (${data.vat_pct}% incl.)`} value={formatCurrency(totals.tax)} />
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-gray-500 text-sm">Loading…</div>
        ) : error ? (
          <div className="p-10 text-center text-red-400 text-sm">Failed to load report.</div>
        ) : (data?.rows?.length ?? 0) === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm">
            No sales in this period for the selected filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-400 uppercase tracking-wider border-b border-white/5">
                <tr>
                  {groupBy === 'item' ? (
                    <>
                      <th className="text-left px-4 py-3 font-semibold">SKU</th>
                      <th className="text-left px-4 py-3 font-semibold">Description</th>
                      <th className="text-left px-4 py-3 font-semibold">Category</th>
                    </>
                  ) : (
                    <th className="text-left px-4 py-3 font-semibold">Category</th>
                  )}
                  <th className="text-right px-4 py-3 font-semibold">Units</th>
                  <th className="text-right px-4 py-3 font-semibold">Cost</th>
                  <th className="text-right px-4 py-3 font-semibold">Gross Rev.</th>
                  <th className="text-right px-4 py-3 font-semibold">Discount</th>
                  <th className="text-right px-4 py-3 font-semibold">Net Rev.</th>
                  <th className="text-right px-4 py-3 font-semibold">Tax</th>
                </tr>
              </thead>
              <tbody>
                {groupBy === 'item'
                  ? (data!.rows as ItemRow[]).map(r => (
                      <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-2.5 font-mono text-white text-xs">{r.sku}</td>
                        <td className="px-4 py-2.5 text-white max-w-md truncate">{r.name_en}</td>
                        <td className="px-4 py-2.5 text-gray-400 text-xs">{r.category_name_en ?? '—'}</td>
                        <td className="px-4 py-2.5 text-right text-white tabular-nums">{r.units_sold}</td>
                        <td className="px-4 py-2.5 text-right text-gray-300 tabular-nums">{formatCurrency(r.cost_fils)}</td>
                        <td className="px-4 py-2.5 text-right text-white tabular-nums">{formatCurrency(r.gross_revenue_fils)}</td>
                        <td className="px-4 py-2.5 text-right text-yellow-300 tabular-nums">{r.discount_fils > 0 ? `−${formatCurrency(r.discount_fils)}` : '—'}</td>
                        <td className="px-4 py-2.5 text-right text-white font-semibold tabular-nums">{formatCurrency(r.net_revenue_fils)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-400 tabular-nums">{formatCurrency(r.tax_fils)}</td>
                      </tr>
                    ))
                  : (data!.rows as CategoryRow[]).map(r => (
                      <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-2.5 text-white">{r.name_en}</td>
                        <td className="px-4 py-2.5 text-right text-white tabular-nums">{r.units_sold}</td>
                        <td className="px-4 py-2.5 text-right text-gray-300 tabular-nums">{formatCurrency(r.cost_fils)}</td>
                        <td className="px-4 py-2.5 text-right text-white tabular-nums">{formatCurrency(r.gross_revenue_fils)}</td>
                        <td className="px-4 py-2.5 text-right text-yellow-300 tabular-nums">{r.discount_fils > 0 ? `−${formatCurrency(r.discount_fils)}` : '—'}</td>
                        <td className="px-4 py-2.5 text-right text-white font-semibold tabular-nums">{formatCurrency(r.net_revenue_fils)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-400 tabular-nums">{formatCurrency(r.tax_fils)}</td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[11px] text-gray-500 mt-3 leading-relaxed max-w-3xl">
        Cost uses the WAC snapshot from <code className="text-gray-400">stock_movements</code> captured at the moment of each sale, so historical numbers stay stable even after later PO receives shift <code className="text-gray-400">products.cost_fils</code>.
        Revenue rows exclude cancelled orders. Discount includes per-line discounts only; order-level discounts (codes, gift cards, store credit) are on the Sales Summary tab.
        Tax is derived from Net at the VAT-inclusive rate configured under Settings → JoFotara.
      </p>
    </div>
  )
}

function TotalTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">{label}</div>
      <div className="text-sm text-white font-semibold tabular-nums">{value}</div>
    </div>
  )
}
